import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { env } from '@/config/env';
import { imagesApi } from '@/lib/api/client';
import { DrawerShell, Empty, ListSkeleton } from './item-drawers';
import {
  MEDIA_TYPE,
  toItemInput,
  useAddItemMedia,
  useUpdateItem,
  type MasterItem,
} from './item-master.api';

/**
 * Borrow a product's details from the shared image service.
 *
 * A separate service from the API, keyed by subscription rather than by the
 * session token, so it has its own client. The search opens on the item's
 * barcode, which is what usually finds it.
 *
 * Nothing is copied until it is asked for: the three switches decide whether a
 * match contributes its picture, its names or its search terms.
 */

interface MetadataMatch {
  id: string;
  itemGtin: string | null;
  itemBrand: string | null;
  itemName: string | null;
  itemDescription: string | null;
  itemSearchTerms: string | null;
}

/** The service serves its pictures by id, with the key on the query string. */
function imageUrlFor(id: string) {
  return `${env.imagesUrl.replace(/\/$/, '')}/query/photos-sm/${id}?subscription-key=${env.imagesKey}`;
}

function useMetadataSearch(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['item-metadata', term],
    queryFn: async () => {
      const response = await imagesApi.get<MetadataMatch[]>(
        `search?searchTerm=${encodeURIComponent(term)}`
      );
      return response.data ?? [];
    },
    enabled: enabled && term.trim().length > 0,
  });
}

export function MetadataSheet({
  item,
  open,
  onOpenChange,
}: {
  item: MasterItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [seeded, setSeeded] = useState<string | null>(null);
  const [useImage, setUseImage] = useState(true);
  const [useName, setUseName] = useState(true);
  const [useTerms, setUseTerms] = useState(true);

  const update = useUpdateItem();
  const addMedia = useAddItemMedia(item?.id ?? null);

  // Opens on the barcode, which is what identifies the product to the service.
  if (item && seeded !== item.id) {
    setSeeded(item.id);
    setTerm(item.gtin ?? '');
    setQuery(item.gtin ?? '');
  }

  const search = useMetadataSearch(query, open);
  const rows = search.data ?? [];
  const busy = update.isPending || addMedia.isPending;

  async function apply(match: MetadataMatch) {
    if (!item) return;
    if (!useImage && !useName && !useTerms) {
      toast.error(t('itemMaster.pickWhatToCopy'));
      return;
    }

    try {
      if (useImage) {
        const url = imageUrlFor(match.id);
        await addMedia.mutateAsync({
          url,
          thumbnailUrl: url,
          type: MEDIA_TYPE.picture,
        });
      }

      const input = toItemInput(item);
      if (useName) {
        input.name = match.itemName;
        input.brand = (match.itemBrand ?? '').toUpperCase() || null;
        input.description = match.itemDescription;
      }
      if (useTerms) {
        input.searchTags = (match.itemSearchTerms ?? '').toUpperCase() || null;
      }
      if (useName || useTerms) {
        await update.mutateAsync({ id: item.id, input });
      }

      toast.success(t('itemMaster.saved'));
      onOpenChange(false);
    } catch {
      toast.error(t('itemMaster.actionFailed'));
    }
  }

  return (
    <DrawerShell
      title={t('itemMaster.productMetadata')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(term.trim());
        }}
      >
        <Input
          value={term}
          placeholder={t('itemMaster.searchPlaceholder')}
          onChange={(event) => setTerm(event.target.value)}
        />
        <Button type="submit" variant="outline" disabled={search.isFetching}>
          {search.isFetching ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
        </Button>
      </form>

      <div className="space-y-2 rounded-xl border p-3">
        <p className="text-sm font-medium">{t('itemMaster.whatToCopy')}</p>
        {(
          [
            [t('itemMaster.media'), useImage, setUseImage],
            [t('itemMaster.name'), useName, setUseName],
            [t('itemMaster.searchTags'), useTerms, setUseTerms],
          ] as const
        ).map(([label, checked, set]) => (
          <label key={label} className="flex items-center justify-between gap-3">
            <span className="text-sm">{label}</span>
            <Switch checked={checked} onCheckedChange={set} />
          </label>
        ))}
      </div>

      {search.isLoading && <ListSkeleton />}
      {search.isError && <Empty>{t('itemMaster.metadataFailed')}</Empty>}
      {search.isSuccess && rows.length === 0 && (
        <Empty>{t('itemMaster.noResults')}</Empty>
      )}

      {rows.map((match) => (
        <div key={match.id} className="space-y-2 rounded-xl border p-3">
          <div className="flex gap-3">
            <img
              src={imageUrlFor(match.id)}
              alt=""
              className="size-20 shrink-0 rounded-lg border object-contain"
            />
            <div className="min-w-0 flex-1 space-y-0.5 text-sm">
              <div className="flex items-center gap-1">
                <span className="truncate font-mono text-xs">
                  {match.itemGtin}
                </span>
                {match.itemGtin && <CopyButton value={match.itemGtin} />}
              </div>
              <p className="truncate font-medium">
                {[match.itemBrand, match.itemName].filter(Boolean).join(' ')}
              </p>
              <p className="text-muted-foreground line-clamp-2">
                {match.itemDescription}
              </p>
            </div>
          </div>

          {match.itemSearchTerms && (
            <p className="text-muted-foreground line-clamp-2 text-xs">
              {match.itemSearchTerms}
            </p>
          )}

          <Button
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => void apply(match)}
          >
            {busy && <Loader2 className="animate-spin" />}
            {t('itemMaster.useThis')}
          </Button>
        </div>
      ))}
    </DrawerShell>
  );
}
